// Achievements sort and filter module: fields, options, and the sort function.

import type { AchievementTrackProgress } from '@airhead-games/shared/types';
import type { SortOption } from '../../../components/collectionToolbar';
import {
  applySortDirection,
  type SortDirection,
  toTimestamp,
} from '../../../utilities/sort';
import {
  currentMilestone,
  isTrackComplete,
  trackProgress,
} from './achievementTrack';

export const ACHIEVEMENT_STATUSES = ['incomplete', 'completed'] as const;
export type AchievementStatusFilter = (typeof ACHIEVEMENT_STATUSES)[number];

export const ACHIEVEMENT_STATUS_LABELS: Record<
  AchievementStatusFilter,
  string
> = {
  incomplete: 'Incomplete',
  completed: 'Completed',
};

export const trackStatus = (
  track: AchievementTrackProgress
): AchievementStatusFilter =>
  isTrackComplete(track) ? 'completed' : 'incomplete';

// A track has at most 10 milestones, so levels run from 1 to 10.
export const ACHIEVEMENT_LEVELS = Array.from(
  { length: 10 },
  (_, index) => index + 1
);

export const parseLevel = (raw: string): number | undefined => {
  const level = Number(raw);
  return Number.isInteger(level) && level >= 1 && level <= 10
    ? level
    : undefined;
};

export const ACHIEVEMENT_SORT_FIELDS = [
  'addedDate',
  'earnedDate',
  'alphabetical',
  'progress',
  'rewardAmount',
] as const;
export type AchievementSortField = (typeof ACHIEVEMENT_SORT_FIELDS)[number];

export const DEFAULT_ACHIEVEMENT_SORT_FIELD: AchievementSortField = 'progress';
export const DEFAULT_ACHIEVEMENT_SORT_DIRECTION: SortDirection = 'asc';

export const ACHIEVEMENT_SORT_OPTIONS: SortOption<AchievementSortField>[] = [
  {
    value: 'addedDate',
    label: 'Added Date',
    directionLabels: { desc: 'Newest', asc: 'Oldest' },
  },
  {
    value: 'earnedDate',
    label: 'Earned Date',
    directionLabels: { desc: 'Newest', asc: 'Oldest' },
  },
  {
    value: 'alphabetical',
    label: 'Alphabetical',
    directionLabels: { asc: 'A > Z', desc: 'Z > A' },
  },
  {
    value: 'progress',
    label: 'Progress',
    directionLabels: { desc: 'Most', asc: 'Least' },
  },
  {
    value: 'rewardAmount',
    label: 'Reward Amount',
    directionLabels: { desc: 'Most', asc: 'Least' },
  },
];

const nextRewardAmount = (track: AchievementTrackProgress): number | null =>
  isTrackComplete(track) ? null : currentMilestone(track).reward.amount;

// Compares two optional sort keys. A missing key (nothing earned, no next
// reward) sorts last in either direction.
const compareOptional = (
  a: number | null,
  b: number | null,
  direction: SortDirection
): number => {
  if (a === null || b === null) {
    return a === b ? 0 : a === null ? 1 : -1;
  }
  return applySortDirection(a - b, direction);
};

export const sortAchievementTracks = (
  tracks: AchievementTrackProgress[],
  field: AchievementSortField,
  direction: SortDirection
): AchievementTrackProgress[] =>
  [...tracks].sort((a, b) => {
    let primary = 0;
    if (field === 'addedDate') {
      primary = compareOptional(
        a.liveAt ? toTimestamp(a.liveAt) : null,
        b.liveAt ? toTimestamp(b.liveAt) : null,
        direction
      );
    } else if (field === 'earnedDate') {
      primary = compareOptional(
        a.lastEarnedAt ? toTimestamp(a.lastEarnedAt) : null,
        b.lastEarnedAt ? toTimestamp(b.lastEarnedAt) : null,
        direction
      );
    } else if (field === 'alphabetical') {
      primary = applySortDirection(a.title.localeCompare(b.title), direction);
    } else if (field === 'progress') {
      primary = applySortDirection(
        trackProgress(a) - trackProgress(b),
        direction
      );
    } else {
      primary = compareOptional(
        nextRewardAmount(a),
        nextRewardAmount(b),
        direction
      );
    }

    return primary !== 0 ? primary : a.title.localeCompare(b.title);
  });
